import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  fetchPublicProviderProfile,
  toApiAbsoluteUrl,
  catalogApi,
} from "../api/client";
import ListingCard from "../Components/ListingCard";
import SEO from "../Components/Seo";

const ProviderProfileById = () => {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false); // <-- modal state

  useEffect(() => {
    const fetchProvider = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id || typeof id !== "string" || id.trim().length === 0) {
          throw Object.assign(new Error("INVALID_ID"), {
            status: 400,
            data: { message: "Invalid provider id in URL" },
          });
        }
        const profile = await fetchPublicProviderProfile(id);
        setProvider(profile || null);

        if (profile?.userId) {
          try {
            const res = await catalogApi.publicSearch({
              providerUserId: profile.userId,
              limit: 20,
              page: 1,
              sort: "created_desc",
            });
            setListings(Array.isArray(res?.items) ? res.items : []);
          } catch (e) {
            setListings([]);
          }
        } else {
          setListings([]);
        }
      } catch (err) {
        const msg =
          err?.data?.message ||
          (err?.status === 404
            ? "Provider not found."
            : "Failed to load provider.");
        setError(`${msg}${err?.status ? ` (HTTP ${err.status})` : ""}`);
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id]);

  if (loading) return <p>Loading provider...</p>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (!provider) return <p>No provider found</p>;

  const firstDocUrl = provider.documents?.[0]?.url;
  const firstListingImageUrl =
    Array.isArray(listings) && listings.length > 0
      ? Array.isArray(listings[0]?.images)
        ? listings[0].images[0]?.url
        : null
      : null;
  const coverCandidate = firstDocUrl || firstListingImageUrl;
  const displayImage = coverCandidate
    ? toApiAbsoluteUrl(coverCandidate)
    : "https://placehold.co/800x400/png?text=No+Image";

  const displayAddress =
    provider.address ||
    (Array.isArray(listings) && listings[0]?.address) ||
    null;

  return (
    <>
      <SEO title={`${provider.companyName} - Online Indian Market (OIM)`} />
      <SEO
        description={`${provider.companyName} is a manufacturer, wholesaler, retailer, seller, and global buyer.`}
      />
      <SEO
        keywords={`${provider.companyName}, manufacturer, wholesaler, retailer, seller, global buyer`}
      />
      <SEO author={`${provider.companyName}`} />
      <SEO url={`https://www.onlineindianmarket.com/${provider.id}`} />
      <SEO image={displayImage} />
      <SEO categories={[provider.companyType]} />

      <div className="w-11/12 poppins-regular mx-auto p-4 space-y-6">
        {/* Cover Image + QR Code */}
        <div className="relative w-full h-64 bg-gray-200 rounded-lg overflow-hidden">
          <img
            src={displayImage}
            alt={provider.companyName}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute top-4 right-4 bg-white p-2 rounded shadow cursor-pointer"
            onClick={() => setShowQRModal(true)} // <-- open modal
          >
            <QRCodeCanvas
              value={window.location.href}
              size={200}
              level="H"
              includeMargin
            />
          </div>
        </div>

        {/* QR Modal */}
        {showQRModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
            onClick={() => setShowQRModal(false)}
          >
            <div
              className="bg-white p-4 rounded-lg"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking modal content
            >
              <QRCodeCanvas
                value={window.location.href}
                size={400}
                level="H"
                includeMargin
              />
              <button
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => setShowQRModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Profile Info */}
        <div className="space-y-2">
          <h2 className="text-3xl uppercase tracking-wider font-bold">
            {provider.companyName}
          </h2>
          {provider.companyType && (
            <p className="text-gray-600 capitalize">{provider.companyType}</p>
          )}
          {displayAddress && <p>📍 {displayAddress}</p>}
          {provider.phoneNumber && <p>📞 {provider.phoneNumber}</p>}
          {provider.email && (
            <p>
              ✉️{" "}
              <a
                href={`mailto:${provider.email}`}
                className="text-blue-600 hover:underline"
              >
                {provider.email}
              </a>
            </p>
          )}
          {provider.gstNumber && <p>GST: {provider.gstNumber}</p>}
          {provider.panNumber && <p>PAN: {provider.panNumber}</p>}

          {/* New: About Company (one-time editable) */}
          {provider.aboutCompany && (
            <div className="pt-4">
              <h3 className="font-semibold">About Company</h3>
              <p className="text-gray-700 text-justify whitespace-pre-line">
                {provider.aboutCompany}
              </p>
            </div>
          )}

          {/* New: Why Choose Us (one-time editable) */}
          {provider.whyChooseUs && (
            <div className="pt-3">
              <h3 className="font-semibold">Why Choose Us</h3>
              <p className="text-gray-700 text-justify whitespace-pre-line">
                {provider.whyChooseUs}
              </p>
            </div>
          )}

          {/* Optional documents */}
          {/* {provider.documents?.length > 0 && (
          <div className="pt-4">
            <h3 className="font-semibold">Documents</h3>
            <ul className="list-disc list-inside">
              {provider.documents.map((doc) => (
                <li key={doc.id}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {doc.originalName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )} */}
        </div>

        {/* Provider Listings */}
        <div className="pt-4">
          <h3 className="text-2xl font-semibold mb-3">
            Listings by {provider.companyName}
          </h3>
          {Array.isArray(listings) && listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {listings.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          ) : (
            <p>No listings yet.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default ProviderProfileById;
